import { useRef, useState } from "react";
import { User as UserIcon } from "lucide-react";
import {
  Button,
  SubmitButton,
  SubmittingHint,
  useSubmittingMutation,
  useToast,
} from "@/design-system/components/ui";
import { portalAuthApi } from "../api/auth";
import { PortalApiError } from "../api/client";
import { usePortalAuth } from "../PortalAuthContext";
import { PortalChangePasswordForm } from "../components/PortalChangePasswordForm";
import { VendorProfileBlock } from "../components/VendorProfileBlock";

// External Portal v2 §4.5 — Account & settings tab. Profile (read-only
// today) and a request-a-password-reset-email action. (MFA was removed
// from the portal in Round-3.)

export function AccountSettingsTab() {
  const auth = usePortalAuth();
  const me = auth.me;
  if (!me) return null;

  return (
    <section className="flex flex-col gap-space-6" aria-label="Account & settings">
      <div
        className="p-space-4 border border-border-light rounded-md bg-white-pure"
        data-testid="settings-account"
      >
        <h3 className="text-h4 font-display text-onyx m-0">Your account</h3>
        <p className="text-small text-gray-mid mt-space-1">
          {me.email} · {me.accountType} account
        </p>
      </div>

      <ProfileBlock />
      {/* Step 5 W6 — Vendor-RECORD fields, kept separate from the PortalUser
          block above. Vendors only. */}
      {me.accountType === "Vendor" && <VendorProfileBlock />}
      {me.accountType === "Broker" && <PhotoBlock />}
      <PasswordBlock />
    </section>
  );
}

// UAT FixBatch FIND-16 — broker profile picture. File picker + preview +
// upload to POST /api/portal/auth/photo; on success refetch /me so the
// top-bar avatar updates.
function PhotoBlock() {
  const auth = usePortalAuth();
  const toast = useToast();
  const inputRef = useRef<HTMLInputElement>(null);
  const [file, setFile] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [done, setDone] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const uploadPhoto = useSubmittingMutation(
    async (f: File) => {
      await portalAuthApi.uploadPhoto(f);
    },
    {
      refresh: auth.refresh,
      onSuccess: () => {
        setDone(true);
        setFile(null);
        setPreviewUrl(null);
        if (inputRef.current) inputRef.current.value = "";
        toast.success("Profile picture updated.");
      },
      onError: (err) => {
        const message =
          err instanceof PortalApiError
            ? err.message
            : err instanceof Error
              ? err.message
              : "Couldn't upload your photo. Try again.";
        setError(message);
        toast.error(message);
      },
    },
  );

  if (!auth.me) return null;
  const currentUrl = previewUrl ?? auth.me.avatarUrl ?? null;

  function onPick(e: React.ChangeEvent<HTMLInputElement>) {
    const picked = e.target.files?.[0] ?? null;
    setFile(picked);
    setDone(false);
    setError(null);
    setPreviewUrl(picked ? URL.createObjectURL(picked) : null);
  }

  function handleUpload() {
    if (!file) return;
    setDone(false);
    setError(null);
    void uploadPhoto.submit(file);
  }

  return (
    <div
      className="p-space-4 border border-border-light rounded-md bg-white-pure"
      data-testid="settings-photo"
    >
      <h3 className="text-h4 font-display text-onyx m-0">Profile picture</h3>
      <p className="text-small text-gray-mid mt-space-1">
        This photo shows next to your name in the portal. Use a square JPEG,
        PNG, WebP, or GIF.
      </p>
      <div className="mt-space-3 flex items-center gap-space-4">
        {currentUrl ? (
          <img
            src={currentUrl}
            alt="Profile preview"
            className="h-16 w-16 rounded-pill object-cover border border-border-light"
          />
        ) : (
          <span className="h-16 w-16 rounded-pill bg-white-warm border border-border-light flex items-center justify-center text-gray-mid">
            <UserIcon size={24} aria-hidden />
          </span>
        )}
        <div className="flex flex-col gap-space-2">
          <input
            ref={inputRef}
            type="file"
            accept="image/jpeg,image/png,image/webp,image/gif"
            onChange={onPick}
            data-testid="settings-photo-input"
            className="text-small"
          />
          <SubmitButton
            type="button"
            variant="secondary"
            size="sm"
            onClick={handleUpload}
            pending={uploadPhoto.pending}
            pendingLabel="Uploading…"
            disabled={!file}
            data-testid="settings-photo-upload"
          >
            Upload
          </SubmitButton>
          <SubmittingHint pending={uploadPhoto.pending} />
        </div>
      </div>
      {done && (
        <p className="text-small text-status-success mt-space-2" role="status">
          Photo updated.
        </p>
      )}
      {error && (
        <p className="text-small text-status-error mt-space-2" role="alert">
          {error}
        </p>
      )}
    </div>
  );
}

function ProfileBlock() {
  const auth = usePortalAuth();
  if (!auth.me) return null;
  return (
    <div
      className="p-space-4 border border-border-light rounded-md bg-white-pure"
      data-testid="settings-profile"
    >
      <h3 className="text-h4 font-display text-onyx m-0">Profile</h3>
      <p className="text-small text-gray-mid mt-space-1">
        Need to change your name on file? Email{" "}
        <a className="underline" href="mailto:support@phoenixcapital.solutions">
          support@phoenixcapital.solutions
        </a>{" "}
        and we'll update it.
      </p>
      <dl className="mt-space-3 grid grid-cols-1 sm:grid-cols-2 gap-space-3">
        <div>
          <dt className="text-caption text-gray-mid">Full name</dt>
          <dd className="text-small text-onyx m-0">{auth.me.fullName}</dd>
        </div>
        <div>
          <dt className="text-caption text-gray-mid">Email</dt>
          <dd className="text-small text-onyx m-0">{auth.me.email}</dd>
        </div>
        <div>
          <dt className="text-caption text-gray-mid">Account type</dt>
          <dd className="text-small text-onyx m-0">{auth.me.accountType}</dd>
        </div>
      </dl>
    </div>
  );
}

function PasswordBlock() {
  const auth = usePortalAuth();
  const toast = useToast();
  const [state, setState] = useState<"idle" | "sending" | "sent" | "error">("idle");
  const [error, setError] = useState<string | null>(null);

  async function handleClick() {
    if (!auth.me) return;
    setState("sending");
    setError(null);
    try {
      await portalAuthApi.passwordResetRequest(auth.me.email);
      setState("sent");
      toast.success("Reset link sent — check your email.");
    } catch (err) {
      setState("error");
      const message =
        err instanceof PortalApiError
          ? err.message
          : err instanceof Error
            ? err.message
            : "Couldn't send the reset email. Try again in a minute.";
      setError(message);
      toast.error(message);
    }
  }

  return (
    <div
      className="p-space-4 border border-border-light rounded-md bg-white-pure"
      data-testid="settings-password"
    >
      <h3 className="text-h4 font-display text-onyx m-0">Password</h3>

      {/* Change your password in place (no email round-trip). */}
      <p className="text-small text-gray-mid mt-space-1">
        Change your password here — enter your current one, then choose a new
        password of at least 12 characters.
      </p>
      <div className="mt-space-3" data-testid="settings-change-password">
        <PortalChangePasswordForm submitLabel="Change password" />
      </div>

      {/* Fallback for a forgotten password: email a reset link. */}
      <div className="mt-space-5 pt-space-4 border-t border-border-light">
        <p className="text-small text-gray-mid m-0">
          Forgot your current password? We'll email you a link to set a new one.
          The link expires in about two hours.
        </p>
        <div className="mt-space-3">
          <Button
            type="button"
            variant="secondary"
            size="sm"
            onClick={() => void handleClick()}
            disabled={state === "sending"}
            data-testid="settings-password-button"
          >
            {state === "sending" ? "Sending…" : "Send reset link"}
          </Button>
        </div>
        {state === "sent" && (
          <p
            className="text-small text-status-success mt-space-2"
            role="status"
            data-testid="settings-password-sent"
          >
            Reset link sent — check your email. Send another if it didn't arrive.
          </p>
        )}
        {state === "error" && error && (
          <p className="text-small text-status-error mt-space-2" role="alert">
            {error}
          </p>
        )}
      </div>
    </div>
  );
}
